import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, message } from "../api";

type PublicNaming = "initial" | "full" | "heading";

interface FrameSection { heading: string; body: string }

interface MeetingTypeFull {
  id: string; clientId: string; clientName: string; code: string; name: string;
  frame: { sections: FrameSection[] } | null;
  hasDocument: boolean;
  promptNotes: string | null;
  filenamePattern: string;
  surnameOnly: boolean;
  publicNaming: PublicNaming;
  active: boolean;
  bodyIds: string[];
  clientBodies: { id: string; name: string; abbrev: string }[];
  usualStaffIds: string[];
  clientStaff: { id: string; name: string; title: string | null }[];
}

/** A file as base64, for a JSON upload. */
async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function MeetingTypePage() {
  const { id } = useParams<{ id: string }>();
  const [t, setT] = useState<MeetingTypeFull | null>(null);
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [pattern, setPattern] = useState("");
  const [bodyIds, setBodyIds] = useState<string[]>([]);
  const [usualStaffIds, setUsualStaffIds] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [surnameOnly, setSurnameOnly] = useState(false);
  const [publicNaming, setPublicNaming] = useState<PublicNaming>("initial");
  const [learning, setLearning] = useState(false);
  const [learned, setLearned] = useState<{ rules: string; surnameOnly: boolean; publicNaming: PublicNaming; examples: number } | null>(null);
  const [learnError, setLearnError] = useState<string | null>(null);
  const examplesRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [frameNote, setFrameNote] = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    if (!id) return;
    api.get<MeetingTypeFull>(`/api/meeting-types/${id}`)
      .then((row) => {
        setT(row);
        setNotes(row.promptNotes ?? "");
        setName(row.name);
        setCode(row.code);
        setPattern(row.filenamePattern);
        setBodyIds(row.bodyIds);
        setUsualStaffIds(row.usualStaffIds);
        setActive(row.active);
        setSurnameOnly(row.surnameOnly);
        setPublicNaming(row.publicNaming);
      })
      .catch((e) => setError(message(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (bodyIds.length === 0) { setError("Choose at least one body: it sets the titles the minutes use."); return; }
    setBusy(true); setError(null); setSaved(false);
    try {
      await api.patch(`/api/meeting-types/${id}`, {
        name, code, bodyIds, active, surnameOnly, publicNaming, usualStaffIds,
        filenamePattern: pattern,
        promptNotes: notes.trim() === "" ? null : notes,
      });
      setSaved(true);
      load();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  async function upload(file: File) {
    setFrameNote(null);
    if (!/\.docx$/i.test(file.name)) {
      setFrameNote({ ok: false, text: "Choose a Word .docx file. An older .doc file needs saving as .docx first." });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setFrameNote({ ok: false, text: "That file is over 8 MB. A template is normally well under 1 MB — remove any embedded images and try again." });
      return;
    }
    setUploading(true);
    try {
      const r = await api.put<{ sections: FrameSection[] }>(`/api/meeting-types/${id}/frame`, {
        docxBase64: await toBase64(file),
      });
      setFrameNote({ ok: true, text: `Template stored: ${r.sections.length} sections read from it. Check them below.` });
      load();
    } catch (err) {
      const detail = err instanceof ApiError && typeof err.body.message === "string" ? err.body.message : message(err);
      setFrameNote({ ok: false, text: detail });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  /** Past minutes in, proposed style rules out. Nothing is saved until Save. */
  async function learn(files: File[]) {
    setLearnError(null); setLearned(null);
    const docs = files.filter((f) => /\.docx$/i.test(f.name));
    if (docs.length === 0) { setLearnError("Choose Word .docx files. An older .doc file needs saving as .docx first."); return; }
    if (docs.length > 12) { setLearnError("Twelve at most — the most recent ones are the best guide."); return; }
    if (docs.some((f) => f.size > 5 * 1024 * 1024)) { setLearnError("One of those is over 5 MB. Minutes are normally far smaller."); return; }
    setLearning(true);
    try {
      const r = await api.post<{ rules: string; surnameOnly: boolean; publicNaming: PublicNaming; examples: number }>(
        `/api/meeting-types/${id}/learn-style`,
        { files: await Promise.all(docs.map(async (f) => ({ docxBase64: await toBase64(f) }))) });
      if (notes.trim() === "") { setNotes(r.rules); setSurnameOnly(r.surnameOnly); setPublicNaming(r.publicNaming); setLearned({ ...r, rules: "" }); }
      else setLearned(r);
    } catch (err) {
      setLearnError(err instanceof ApiError && typeof err.body.message === "string" ? err.body.message : message(err));
    } finally {
      setLearning(false);
      if (examplesRef.current) examplesRef.current.value = "";
    }
  }

  function useLearned(how: "replace" | "append") {
    if (!learned) return;
    setNotes(how === "replace" ? learned.rules : `${notes.trimEnd()}\n\n${learned.rules}`);
    setSurnameOnly(learned.surnameOnly);
    setPublicNaming(learned.publicNaming);
    setLearned({ ...learned, rules: "" });
  }

  async function download() {
    try {
      const { url } = await api.get<{ url: string }>(`/api/meeting-types/${id}/frame`);
      window.location.href = url;
    } catch (err) { setError(message(err)); }
  }

  if (error && !t) return <div className="alert" role="alert">{error}</div>;
  if (!t) return <p className="muted">Loading…</p>;

  const toggleBody = (bodyId: string) =>
    setBodyIds((ids) => (ids.includes(bodyId) ? ids.filter((x) => x !== bodyId) : [...ids, bodyId]));

  return (
    <>
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/clients">Clients</Link> <span aria-hidden="true">›</span>{" "}
        <Link to={`/clients/${t.clientId}`}>{t.clientName}</Link>
      </nav>
      <div className="pagehead">
        <div>
          <h1><span className="pill">{t.code}</span> {t.name}</h1>
        </div>
        <Link className="linkbtn" to={`/clients/${t.clientId}`}>← {t.clientName}</Link>
      </div>

      {error && <div className="alert" role="alert">{error}</div>}
      {saved && <div className="notice notice--inline">Saved.</div>}

      <section className="card">
        <h2>Minutes template</h2>
        <p className="muted small">
          The Word file the minutes are written into — the client's own, with its fonts, margins, headers and
          footers — with its headings as the client writes them (numbered, or in capitals on lines of their
          own), the usual wording under each, and <span className="mono">__</span> wherever something is filled
          in. The minutes come out in the same layout, down to the signature block.
        </p>
        {frameNote && (
          <div className={frameNote.ok ? "notice notice--inline" : "alert"} role={frameNote.ok ? "status" : "alert"}>
            {frameNote.text}
          </div>
        )}
        <div className="actions">
          <input ref={fileRef} type="file" hidden
                 accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
          <button className="btn btn--inline" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? "Reading the template…" : t.hasDocument ? "Replace the template" : "Upload a template (.docx)"}
          </button>
          {t.hasDocument && <button className="linkbtn" onClick={() => void download()}>Download the one on file</button>}
        </div>
        {t.frame === null ? (
          <p className="warn">
            No template yet. Minutes can still be drafted, but with a generic structure and no Word file to
            download.
          </p>
        ) : (
          <>
            {!t.hasDocument && (
              <p className="warn small">The headings are on file but the Word document is not — upload it to get a .docx.</p>
            )}
            <p className="muted small">
              {t.frame.sections.length} sections read from the template. To change them, edit the Word file and
              upload it again.
            </p>
            <ol className="frame">
              {t.frame.sections.map((s, i) => (
                <li key={i}>
                  <strong>{s.heading}</strong>
                  {s.body && <pre className="frame__body">{s.body}</pre>}
                </li>
              ))}
            </ol>
          </>
        )}
      </section>

      <form className="card card--form" onSubmit={save}>
        <h2>Settings</h2>
        <div className="field">
          <label htmlFor="mtname">Name</label>
          <input id="mtname" value={name} required onChange={(e) => setName(e.target.value)} />
          <p className="hint">As it is said in the meeting — "Regular Session", "Work Session". This is how a meeting of this kind is found in a recording.</p>
        </div>
        <div className="field">
          <label htmlFor="mtcode">Code</label>
          <input id="mtcode" value={code} required className="mono" maxLength={12} pattern="[A-Za-z0-9.]+"
                 onChange={(e) => setCode(e.target.value)} />
          <p className="hint">A short tag, used in file names. Letters, numbers and full stops.</p>
        </div>
        <fieldset className="field checks">
          <legend>Bodies meeting</legend>
          {t.clientBodies.length === 0
            ? <p className="warn small">This client has no bodies yet. Add one on the client page first.</p>
            : t.clientBodies.map((b) => (
              <label key={b.id} className="check">
                <input type="checkbox" checked={bodyIds.includes(b.id)} onChange={() => toggleBody(b.id)} />
                <span>{b.name} <span className="muted">({b.abbrev})</span></span>
              </label>
            ))}
          <p className="hint">Whoever is seated on these bodies is addressed by their title there. A joint meeting has two.</p>
        </fieldset>
        {t.clientStaff.length > 0 && (
          <fieldset className="field checks">
            <legend>Staff who attend as a rule</legend>
            {t.clientStaff.map((p) => (
              <label key={p.id} className="check">
                <input type="checkbox" checked={usualStaffIds.includes(p.id)}
                       onChange={() => setUsualStaffIds(usualStaffIds.includes(p.id)
                         ? usualStaffIds.filter((x) => x !== p.id) : [...usualStaffIds, p.id])} />
                <span>{p.name}{p.title && <span className="muted"> — {p.title}</span>}</span>
              </label>
            ))}
            <p className="hint">
              The roll call lists them as present at every meeting of this kind, along with any other staff the
              recording shows. Many never speak, so the recording alone can miss them. Remove anyone who was away.
            </p>
          </fieldset>
        )}
        <div className="field">
          <label htmlFor="mtpat">Filename pattern</label>
          <input id="mtpat" value={pattern} required className="mono"
                 onChange={(e) => setPattern(e.target.value)} />
          <p className="hint">
            {"{yyyy} {MM} {dd} {code} are substituted, so the default gives "}
            <span className="mono">2026.01.13 {code || "CC"}</span>.
          </p>
        </div>

        <div className="field">
          <span className="fieldlabel">Learn the style from past minutes</span>
          <p className="hint">
            No tip sheet? Choose a few sets of this client's approved minutes for this kind of meeting (.docx — five
            or more recent ones work best). The portal reads how they word motions, record votes and refer to people,
            and writes the style rules for you to check. The files themselves are not kept.
          </p>
          <input ref={examplesRef} type="file" hidden multiple
                 accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                 onChange={(e) => { const f = [...(e.target.files ?? [])]; if (f.length) void learn(f); }} />
          <div className="actions">
            <button className="btn btn--inline btn--ghost" type="button" disabled={learning}
                    onClick={() => examplesRef.current?.click()}>
              {learning ? "Reading the minutes… (about half a minute)" : "Choose past minutes"}
            </button>
          </div>
          {learnError && <div className="alert" role="alert">{learnError}</div>}
          {learned && learned.rules === "" && (
            <div className="notice notice--inline" role="status">
              Style rules written from {learned.examples} {learned.examples === 1 ? "set" : "sets"} of minutes. Read
              them through below, correct anything that is off, then Save.
            </div>
          )}
          {learned && learned.rules !== "" && (
            <div className="learned">
              <p className="small"><strong>Proposed from {learned.examples} {learned.examples === 1 ? "set" : "sets"} of minutes.</strong>{" "}
                There are already style rules below.</p>
              <pre className="notes-box notes-box--read">{learned.rules}</pre>
              <div className="actions">
                <button className="btn btn--inline btn--small" type="button" onClick={() => useLearned("replace")}>Replace the rules below</button>
                <button className="linkbtn" type="button" onClick={() => useLearned("append")}>Add after them</button>
                <button className="linkbtn" type="button" onClick={() => setLearned(null)}>Discard</button>
              </div>
            </div>
          )}
        </div>

        <div className="field">
          <label htmlFor="mtnotes">Style rules</label>
          <textarea
            id="mtnotes" rows={16} className="mono small"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="House conventions for this meeting type…"
          />
          <p className="hint">
            Given to the drafter alongside the template. Paste the tip sheet for this kind of meeting here —
            motion wording, how votes are recorded, how staff and members are named.
          </p>
        </div>

        <label className="check">
          <input type="checkbox" checked={surnameOnly} onChange={(e) => setSurnameOnly(e.target.checked)} />
          <span>Surnames alone after first mention <span className="muted">— "Hopper asked…", with no title or Mr./Ms.
            Leave unticked where members keep their title and staff take an honorific.</span></span>
        </label>

        <div className="field">
          <label htmlFor="public-naming">Members of the public who speak</label>
          <select id="public-naming" value={publicNaming} onChange={(e) => setPublicNaming(e.target.value as PublicNaming)}>
            <option value="initial">First name and last initial — "Shelby S."</option>
            <option value="full">Full name, then Mr./Ms. and surname — "Shelby Somerville", then "Ms. Somerville"</option>
            <option value="heading">Name and address in bold, comments on the line below</option>
          </select>
          <p className="hint">
            {publicNaming === "heading"
              ? <>Each speaker's comments start on the line under their name and address in bold —{" "}
                <strong>Shelby Somerville, Elm Street</strong> — and go on "Ms. Somerville noted …". The address is what
                they said, or left as __; Mr./Ms. comes from the honorific set when the speaker is named.</>
              : publicNaming === "full"
                ? <>Mr./Ms. comes from the honorific set when the speaker is named on the meeting page; without one it is left as __.</>
                : <>The name is never shortened further or guessed at: no last name given is "Shelby __".</>}
          </p>
        </div>

        <label className="check">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>In use <span className="muted">— untick to stop looking for this kind of meeting in new recordings</span></span>
        </label>

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </>
  );
}
